// Sound and music, all made in the browser (Web Audio oscillators and a
// little noise) - no audio files to fetch or license. A player's report:
// "We need sound and music."
//
// Two halves. `cuesFor` turns the engine's events into the sounds to play
// (pure, tested); `createSound` plays them, plus a looping tune of our own
// that quickens with the level. The tune must stay well clear of the folk
// song everybody knows from that other falling-block game (README: keep
// clear of its trade dress).
//
// Browsers keep sound off until someone clicks or presses a key on the page
// - a controller button doesn't count - so nothing plays before that.

const PREFS_KEY = 'couchBlocks:sound';

// Each seat sounds a little different, so the room can hear who did what:
// small, in-key steps (semitones), repeating every eight seats.
const SEAT_STEPS = [0, 2, -3, 4, -5, 5, -2, 7];
export const seatPitch = (playerId) => 2 ** ((SEAT_STEPS[(playerId || 0) % SEAT_STEPS.length]) / 12);

// With forty people a batch of events can be enormous; past these, more of
// the same just turns to mush.
const MAX_PER_CUE = 3;
const MAX_CUES = 8;

/**
 * The sounds a batch of engine events makes, in order.
 * [{ cue, playerId?, count?, together?, level? }]
 */
export function cuesFor(events) {
  const shoved = new Set(events.filter((e) => e.type === 'push').map((e) => e.playerId));
  const dropped = new Set(events.filter((e) => e.type === 'drop').map((e) => e.playerId));
  const cues = [];
  const seen = {};
  const add = (cue) => {
    seen[cue.cue] = (seen[cue.cue] || 0) + 1;
    if (seen[cue.cue] <= MAX_PER_CUE) cues.push(cue);
  };
  for (const e of events) {
    if (e.type === 'move' && !shoved.has(e.playerId)) add({ cue: 'move', playerId: e.playerId });
    if (e.type === 'rotate') add({ cue: 'rotate', playerId: e.playerId });
    if (e.type === 'push') add({ cue: 'shove', playerId: e.playerId });
    if (e.type === 'drop') add({ cue: 'drop', playerId: e.playerId });
    // A slammed piece already thudded; the click on top is noise.
    if (e.type === 'lock' && !dropped.has(e.playerId)) add({ cue: 'lock', playerId: e.playerId });
    if (e.type === 'clear') {
      add({
        cue: 'clear', playerId: e.playerId, count: e.count, together: e.owners.length,
      });
    }
    if (e.type === 'level') add({ cue: 'level', level: e.level });
    if (e.type === 'over' && !seen.over) add({ cue: 'over' });
  }
  // The big moments first if there are too many to play.
  const rank = {
    over: 0, level: 1, clear: 2, shove: 3, drop: 4, lock: 5, rotate: 6, move: 7,
  };
  return cues.length <= MAX_CUES ? cues : [...cues].sort((a, b) => rank[a.cue] - rank[b.cue]).slice(0, MAX_CUES);
}

// ---- the tune ----------------------------------------------------------
// Eight bars of eighth notes in C, bright and bouncy: C  Bb  F  C  Am  F  G  G.
// Written for Couch Blocks. MIDI note numbers; null is a rest.
const CHORDS = [
  [48, 52, 55], [46, 50, 53], [41, 45, 48], [48, 52, 55],
  [45, 48, 52], [41, 45, 48], [43, 47, 50], [43, 47, 50],
];
const MELODY = [
  72, null, 76, null, 79, 76, 74, null,
  74, null, 77, null, 74, null, 70, null,
  69, 72, 77, null, 76, null, 72, null,
  72, null, null, null, 67, null, 72, null,
  76, null, 72, 76, 81, null, 79, null,
  77, null, 76, null, 74, null, 72, null,
  74, null, 79, null, 83, null, 81, 79,
  79, null, null, null, null, null, 74, 76,
];
const STEPS = MELODY.length;
const hz = (midi) => 440 * 2 ** ((midi - 69) / 12);

// A gentle start that quickens a little each level, up to a cap.
export const tempoFor = (level) => Math.min(168, 104 + (Math.max(1, level) - 1) * 6);

function readPrefs(storage) {
  try {
    const p = JSON.parse(storage?.getItem(PREFS_KEY));
    if (p) return { sfx: p.sfx !== false, music: p.music !== false, last: p.last || null };
  } catch { /* fine */ }
  return { sfx: true, music: true, last: null };
}

/**
 * The player. Everything is a no-op until `unlock()` has run from a click or
 * key press, or where there's no Web Audio at all.
 */
export function createSound({
  AudioCtx = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null,
  storage = typeof window !== 'undefined' ? window.localStorage : null,
  onChange = () => {},
} = {}) {
  const prefs = readPrefs(storage);
  let ctx = null;
  let master = null;
  let sfxBus = null;
  let musicBus = null;
  let noise = null;
  let music = null; // { timer, step, next } while the tune is playing
  let level = 1;

  const save = () => {
    try { storage?.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch { /* fine */ }
    onChange(api.state());
  };

  function unlock() {
    if (!AudioCtx) return;
    if (!ctx) {
      try { ctx = new AudioCtx(); } catch { AudioCtx = null; return; }
      master = ctx.createGain();
      master.gain.value = 0.8;
      master.connect(ctx.destination);
      sfxBus = ctx.createGain();
      sfxBus.gain.value = prefs.sfx ? 1 : 0;
      sfxBus.connect(master);
      musicBus = ctx.createGain();
      musicBus.gain.value = 0; // faded in by update()
      musicBus.connect(master);
      ctx.onstatechange = () => onChange(api.state());
      // Half a second of white noise, for thuds and shoves.
      noise = ctx.createBuffer(1, Math.floor(ctx.sampleRate / 2), ctx.sampleRate);
      const data = noise.getChannelData(0);
      for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
    }
    if (ctx.state === 'suspended') ctx.resume?.();
    onChange(api.state());
  }
  const ready = () => Boolean(ctx) && ctx.state !== 'closed';

  // One note: an oscillator through an envelope.
  function tone(bus, {
    freq, at = 0, dur = 0.08, type = 'square', vol = 0.1, to = null, attack = 0.004,
  }) {
    const t = ctx.currentTime + at;
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (to) osc.frequency.exponentialRampToValueAtTime(to, t + dur);
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(vol, t + attack);
    env.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(env);
    env.connect(bus);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }
  function hiss({ at = 0, dur = 0.08, vol = 0.2, cutoff = 800 }) {
    const t = ctx.currentTime + at;
    const src = ctx.createBufferSource();
    src.buffer = noise;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = cutoff;
    const env = ctx.createGain();
    env.gain.setValueAtTime(vol, t);
    env.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter);
    filter.connect(env);
    env.connect(sfxBus);
    src.start(t);
    src.stop(t + dur + 0.02);
  }

  const PENTA = [72, 76, 79, 84, 88]; // C E G C E: a clear climbs these
  const CUES = {
    move: (p) => tone(sfxBus, { freq: 520 * p, dur: 0.03, vol: 0.035 }),
    rotate: (p) => tone(sfxBus, {
      freq: 700 * p, to: 940 * p, dur: 0.05, type: 'triangle', vol: 0.07,
    }),
    lock: (p) => tone(sfxBus, { freq: 196 * p, dur: 0.06, type: 'triangle', vol: 0.14 }),
    drop: (p) => {
      tone(sfxBus, {
        freq: 170 * p, to: 55, dur: 0.14, type: 'sine', vol: 0.4,
      });
      hiss({ dur: 0.07, vol: 0.18, cutoff: 900 });
    },
    shove: (p) => {
      tone(sfxBus, {
        freq: 150 * p, to: 95 * p, dur: 0.1, type: 'sawtooth', vol: 0.09,
      });
      hiss({ dur: 0.05, vol: 0.12, cutoff: 500 });
    },
    // One more note for every line, and a bell for each extra player who
    // had blocks in them - clearing together is the point of the game.
    clear: (p, { count = 1, together = 1 }) => {
      const notes = PENTA.slice(0, Math.min(count, 4) + 1);
      notes.forEach((m, i) => tone(sfxBus, {
        freq: hz(m), at: i * 0.065, dur: 0.16, type: 'square', vol: 0.08,
      }));
      const after = notes.length * 0.065;
      for (let i = 1; i < Math.min(together, 4); i += 1) {
        tone(sfxBus, {
          freq: hz(96), at: after + (i - 1) * 0.09, dur: 0.4, type: 'sine', vol: 0.12,
        });
        tone(sfxBus, {
          freq: hz(103), at: after + (i - 1) * 0.09, dur: 0.4, type: 'sine', vol: 0.06,
        });
      }
    },
    level: () => [67, 72, 76, 79, 84].forEach((m, i) => tone(sfxBus, {
      freq: hz(m), at: 0.25 + i * 0.08, dur: i === 4 ? 0.35 : 0.1, type: 'square', vol: 0.09,
    })),
    over: () => [72, 67, 63, 60].forEach((m, i) => tone(sfxBus, {
      freq: hz(m), at: i * 0.22, dur: i === 3 ? 0.7 : 0.24, type: 'triangle', vol: 0.18,
    })),
  };

  function play(events) {
    if (!ready() || !prefs.sfx || !events?.length) return;
    for (const c of cuesFor(events)) CUES[c.cue](seatPitch(c.playerId), c);
  }
  // Just this computer's own moves - a guest hears its presses straight away
  // rather than a round trip later (the host doesn't send moves).
  function playActions(actions, playerId) {
    if (!ready() || !prefs.sfx) return;
    const p = seatPitch(playerId);
    if (actions.some((a) => a === 'left' || a === 'right')) CUES.move(p);
    if (actions.some((a) => a === 'rotateCW' || a === 'rotateCCW')) CUES.rotate(p);
  }

  // ---- music ----
  const LOOKAHEAD = 0.15;
  function scheduleMusic() {
    const eighth = 30 / tempoFor(level);
    while (music.next < ctx.currentTime + LOOKAHEAD) {
      const i = music.step % STEPS;
      const t = music.next - ctx.currentTime;
      const chord = CHORDS[Math.floor(i / 8)];
      const beat = i % 8;
      // Bass: root on the beat, fifth on the off-beat of two and four.
      if (beat % 2 === 0) {
        tone(musicBus, {
          freq: hz((beat === 2 || beat === 6 ? chord[2] : chord[0]) - 12), at: t, dur: eighth * 1.6, type: 'triangle', vol: 0.22, attack: 0.01,
        });
      }
      // A soft arpeggio running underneath.
      tone(musicBus, {
        freq: hz(chord[[0, 1, 2, 1][beat % 4]] + 12), at: t, dur: eighth * 0.8, type: 'sine', vol: 0.05,
      });
      const m = MELODY[i];
      if (m) {
        let len = 1;
        while (len < 4 && MELODY[(i + len) % STEPS] === null) len += 1;
        tone(musicBus, {
          freq: hz(m), at: t, dur: eighth * len * 0.9, type: 'square', vol: 0.045, attack: 0.008,
        });
      }
      music.step += 1;
      music.next += eighth;
    }
  }
  function startMusic() {
    if (music) return;
    music = { step: 0, next: ctx.currentTime + 0.05 };
    music.timer = setInterval(scheduleMusic, 40);
    scheduleMusic();
  }
  function stopMusic() {
    if (!music) return;
    clearInterval(music.timer);
    music = null;
  }
  function fadeMusic(to, secs) {
    const g = musicBus.gain;
    g.cancelScheduledValues(ctx.currentTime);
    g.setValueAtTime(g.value, ctx.currentTime);
    g.linearRampToValueAtTime(to, ctx.currentTime + secs);
  }

  // Called every frame: music plays only while a game is being played.
  let wanted = false;
  function update(screen, gameLevel) {
    level = gameLevel || 1;
    if (!ready()) return;
    const want = prefs.music && screen === 'playing';
    if (want === wanted) return;
    wanted = want;
    if (want) {
      // Paused and back: carry on. A new game: from the top.
      if (screen === 'playing' && !music) startMusic();
      fadeMusic(0.55, 0.3);
    } else {
      fadeMusic(0, screen === 'over' ? 1.2 : 0.15);
      clearTimeout(stopMusic.timer);
      stopMusic.timer = setTimeout(() => { if (!wanted) stopMusic(); }, 1300);
    }
  }
  // A new game starts the tune from the beginning even if it was playing.
  function restartMusic() {
    if (!ready()) return;
    stopMusic();
    wanted = false;
  }

  function setSfx(on) {
    prefs.sfx = on;
    if (sfxBus) sfxBus.gain.value = on ? 1 : 0;
    save();
  }
  function setMusic(on) {
    prefs.music = on;
    if (!on && ready() && wanted) {
      wanted = false;
      fadeMusic(0, 0.15);
      setTimeout(() => { if (!wanted) stopMusic(); }, 200);
    }
    save();
  }
  // M: everything off, remembering what was on; again puts it back.
  function toggleMute() {
    if (prefs.sfx || prefs.music) {
      prefs.last = { sfx: prefs.sfx, music: prefs.music };
      setSfx(false);
      setMusic(false);
    } else {
      const back = prefs.last || { sfx: true, music: true };
      setSfx(back.sfx);
      setMusic(back.music);
    }
  }

  const api = {
    unlock,
    play,
    playActions,
    update,
    restartMusic,
    setSfx,
    setMusic,
    toggleMute,
    state: () => ({ sfx: prefs.sfx, music: prefs.music, ready: ready() && ctx.state === 'running' }),
    destroy() {
      stopMusic();
      ctx?.close?.();
      ctx = null;
    },
  };
  return api;
}
